
import ShopAPI from "@/common/api/api_shop";
import type { Order, ORDER_STATUSES } from "@/model/entity/order";
import type { Payment } from "@/model/entity/payment";
import type { PaymentCoupon } from "@/model/entity/payment_coupon";
import { PrintBit } from "@/model/entity/printJobSetting";
import type { ReceiptPaper } from "@/model/entity/receipt_paper";
import {
  createEmptyReceiptPaperDetail,
  ReceiptPaperDetail,
  RECEIPT_PAPER_DETAIL_MAX_QUANTITY,
} from "@/model/entity/receipt_paper_detail";
import { Shop } from "@/model/entity/shop";
import type { TableSession } from "@/model/entity/table_session";
import {
  availableQuantity,
  maxIssuableCount,
  receiptCalc,
  RECEIPT_CALC_MESSAGES,
} from "@/model/shop/receiptCalc";
import {
  allocateReceiptOrders,
  receiptProductKey,
} from "@/model/shop/receiptAllocation";
import { TranslateServiceBuilder } from "@/services/Translate/translateServiceBuilder";
import store from "@/store/shop";
import _ from "lodash";
import notificationMixin from "../../mixin/notification";

import Vue from "vue";

const translate = TranslateServiceBuilder.Instance()._;
const api = new ShopAPI();

Vue.directive("enhance-defaulot-value", {
  bind: (el) => {
    el.addEventListener("focus", () => {
      const value = el.value;
      if (value === 0) {
        el.value = "";
      }
    });
    el.addEventListener("blur", () => {
      const value = el.value;
      if (value === "") {
        el.value = 0;
      }
    });
  },
});

enum modeType {
  regist = "regist",
  finish = "finish",
}

export default Vue.extend({
  components: {
    QTranslate: () => import("../../components/elements/QTranslate.vue"),
  },
  mixins: [notificationMixin],
  data() {
    return {
      allSelectedAutoChecked: false,
      count: 0,
      couponDiscountPrice: 0,
      discountPrice: 0,
      errors: {},
      isDiscountChecked: false,
      isFinish: false,
      isLoading: false,
      isOpen: false,
      minReceipts: 0,
      mode: null as modeType,
      modeType: modeType,
      newAdditionalDetail: createEmptyReceiptPaperDetail(),
      orders: [] as Order[],
      payment: undefined,
      paymentCoupons: [] as PaymentCoupon[],
      printedReceiptDetails: [] as ReceiptPaperDetail[],
      receiptPaper: undefined as ReceiptPaper,
      receiptPaperOrders: [] as Order[],
      selectedCoupons: [] as Order[],
      selectedReceiptDetails: [] as ReceiptPaperDetail[],
      selectIssued: [],
      shop: undefined as Shop,
      staffId: store.state.staff.id,
      tableSession: undefined as TableSession,
      taxes: [8, 10],
      // ODR-1704: 追加明細の1枚あたり個数の上限（テンプレートの max 用）
      maxDetailQuantity: RECEIPT_PAPER_DETAIL_MAX_QUANTITY,
      // ODR-1704: 商品プールごとの「1枚あたり個数」。キーは receiptProductKey。
      // 0（未設定）＝この領収書に含めない。DOM走査(changeListChecked)の代わりに状態で持つ。
      productPerReceipt: {} as { [key: string]: number },
      // ODR-1704: 金額の内訳（式）を手動で開いたか。既定は閉じていて、エラー時だけ自動で開く。
      // 手動で開いた場合はエラーが解消しても閉じない（見たくて開いているので勝手に畳まない）。
      isFormulaManuallyOpened: false,
    };
  },
  mounted() {
    api.set_token(store.state.token);
  },
  computed: {
    // ODR-1704: 領収書「1枚あたり」の商品額。チェックした商品プールの 税込単価 × 1枚あたり個数。
    // 総額は この値 × 発行枚数(count) になる。
    price(): number {
      let price = 0;
      for (const selection of this.checkedProductSelections) {
        const unitPrice = Number(selection.units[0] ? selection.units[0].price : 0);
        price += unitPrice * selection.perReceipt;
      }

      return isNaN(price) ? 0 : price;
    },
    prefix(): string {
      return this.$route.params.prefix || "";
    },
    shoppix(): string {
      return this.$route.params.shoppix || "";
    },
    isReceipt(): boolean {
      const setting = store.state.shopSetting;
      if (!setting) {
        return false;
      }

      return setting.is_receipt;
    },
    isReceiptable(): boolean {
      if (this.isLoading) {
        return false;
      }
      // 発行枚数がない
      if (this.count === 0) {
        return false;
      }
      // お預かり(現金)≧0 かつ 残り発行可能≧0 でないと発行不可（ODR-1671）
      const bundleError = this.bundleError();
      if (bundleError) {
        this.showErrorNotification(bundleError);

        return false;
      }
      // 発行可能額（グロス）を超えてはいけない。
      // ODR-1704: 上限が効くのは会計の商品側のみ。追加明細は別決済済みの外部明細で
      // 会計の発行可能額を消費しない（発行枚数ぶん載る明細額は上限側にも同額入るため相殺される）。
      let productIssuePrice = this.price;
      if (this.isDiscountChecked) {
        productIssuePrice -= this.discountPrice;
      }
      if (productIssuePrice * this.count > this.payment.receiptable_price) {
        return false;
      }

      // ODR-1704: お預かり(現金)の判定は receiptCalc（＝bundleError）に一本化した。
      // 以前はここでも自前の式（明細・お釣り込み）で二重判定しており、
      // 警告や発行枚数と条件が食い違っていた。
      return true;
    },
    // 領収書の分割発行計算の入力。式の各項をそのまま画面にも出すため computed に分けている。
    receiptCalcInput() {
      const totalDiscount =
        (this.payment.discount_price || 0) +
        (this.payment.discount_price_by_coupons || 0);
      // 他の領収書で発行済み（消化済み）の分を集計
      let issuedProduct = 0;
      let issuedDiscount = 0;
      for (const rpo of this.receiptPaperOrders) {
        if (rpo.order_id) {
          issuedProduct += rpo.price || 0;
        } else if (rpo.payment_coupon_id) {
          issuedDiscount += -(rpo.discount_price || 0);
        } else {
          issuedDiscount += rpo.discount_price || 0;
        }
      }

      // ODR-1704: price / detailAmount / 金券・割引 は「1枚あたり」なので、今回の発行で
      // 消化する分は発行枚数(count)ぶん。×count しないと複数枚発行しても残額が1枚分しか減らない。
      // 商品だけ×countすると、count=0 のときに「商品は0枚ぶん・金券は満額引く」という
      // 辻褄の合わない計算になるため、金券・割引も同じく×countする。
      // （金券・割引の適用時は count が1枚に固定されるので、正常系では×1で従来と同じ）
      //
      // count=0 は「発行できない状態」だが、そのまま0倍すると今回選択が0円になり
      // 「エラーなのに何も問題なさそうな式」になってしまう。原因を数字で見せたいので、
      // 0枚のときは1枚として評価する（＝1枚出すとどうなるか）。
      const effectiveCount = Math.max(1, this.count);
      const appliedDiscount =
        this.couponDiscountPrice +
        (this.isDiscountChecked ? this.discountPrice : 0);

      return {
        totalProduct: this.payment.total_price || 0,
        totalDiscount,
        issuedProduct,
        issuedDiscount,
        selectedProduct: this.price * effectiveCount,
        appliedDiscount: appliedDiscount * effectiveCount,
      };
    },
    // 領収書の分割発行計算（純粋モジュール receiptCalc に委譲＝バックエンドと同一ロジック）。
    receiptCalcResult() {
      return receiptCalc(this.receiptCalcInput);
    },
    // 残り（会計総額から 発行済み＋今回選択・適用分 を引いた額）。全部選択・適用すると 0 になる。
    remainingValues(): {
      remainingProduct: number;
      remainingDiscount: number;
      remainingIssuable: number;
      hasDiscount: boolean;
    } {
      const totalDiscount =
        (this.payment.discount_price || 0) +
        (this.payment.discount_price_by_coupons || 0);
      const r = this.receiptCalcResult;
      return {
        remainingProduct: r.remainingProduct,
        remainingDiscount: r.remainingDiscount,
        remainingIssuable: r.remainingIssuable,
        hasDiscount: totalDiscount > 0,
      };
    },
    // 領収書金額の内訳（残り商品 − 残り割引 ＝ 残り発行可能）と発行不可の原因ハイライト用。
    receiptFormula(): {
      paymentTotal: number;
      issuedNet: number;
      selectedNet: number;
      remainingProduct: number;
      remainingDiscount: number;
      remainingIssuable: number;
      hasDiscount: boolean;
      reason: string | null;
      discountError: boolean;
      amountError: boolean;
    } {
      const r = this.remainingValues;
      // 何も選択していない間は原因表示を出さない（未選択時のノイズ防止）
      const hasSelection =
        this.price > 0 || this.couponDiscountPrice > 0 || this.isDiscountChecked;
      // 発行不可の理由（bundleError と同一の判定＝表示と実際の可否がズレない）
      const reason = hasSelection ? this.bundleError() : null;
      const input = this.receiptCalcInput;

      // ODR-1704: 「会計 − 発行済 − 今回選択 ＝ 発行可能金額」の各項。
      // ここでの各項は金券・割引を引いた後の「正味（実際に受領した額）」で揃える。
      // (会計−発行済−今回選択) は receiptCalc の remainingIssuable と代数的に同一:
      //   (tP−tD) − (iP−iD) − (sP−aD) = (tP−iP−sP) − (tD−iD−aD) = 残り商品 − 残り割引
      const paymentTotal = input.totalProduct - input.totalDiscount;
      const issuedNet = input.issuedProduct - input.issuedDiscount;
      const selectedNet = input.selectedProduct - input.appliedDiscount;

      return {
        // 会計＝支払い計（商品合計から金券・割引を引いた実受領額）
        paymentTotal,
        issuedNet,
        selectedNet,
        // 発行可能金額の内訳（金券・割引がある時だけ併記）。
        // 「残り商品 − 残り金券・割引 ＝ 発行可能金額」＝ 発行可能金額が何で構成されているか。
        remainingProduct: r.remainingProduct,
        remainingDiscount: r.remainingDiscount,
        remainingIssuable: r.remainingIssuable,
        hasDiscount: r.hasDiscount,
        reason,
        // 発行可能金額がマイナス＝残った商品では金券・割引を使い切れない配分。
        // 引き算の答えがマイナスで見えるので、金券を選ばないと発行できない状況が数字で分かる。
        discountError: !!reason && r.remainingIssuable < 0,
        amountError: !!reason && r.remainingIssuable < 0,
      };
    },
    // ODR-1704: 領収書「1枚あたり」の追加明細額。チェックした明細のみ、単価×1枚あたり個数。
    // 発行枚数ぶん各領収書に載るため、総額は この値 × count になる。
    detailAmount() {
      let amount = 0;
      for (const detail of this.checkedReceiptDetails) {
        amount += Number(detail.unit_price) * Number(detail.quantity);
      }
      return amount;
    },
    /**
     * ODR-1704: お釣りあり金券（PaymentCoupon.coupon_is_change）を使った会計かどうか。
     *
     * お釣りあり金券は「金券額が商品額を超えた分を現金で返す」ため、金券とお釣りは
     * セットで初めて金額が成立する（例: 商品300・金券1,000・お釣り700 → 現金0）。
     * 金券だけを別の領収書に回すと金額が破綻するので、この会計は分割発行を許さず、
     * 金券は全て選択した1枚として発行する。
     */
    hasChangeableCoupon(): boolean {
      return (this.paymentCoupons || []).some(
        (coupon) => coupon.coupon_is_change
      );
    },
    // ODR-1704: この領収書に含める（チェック済みの）追加明細
    checkedReceiptDetails() {
      return this.selectedReceiptDetails.filter((detail) => detail.is_checked);
    },
    /**
     * ODR-1704: 商品プール。同一商品（menu_id＋オプション）は注文をまたいでまとめる。
     *
     * 「飲み放題90分」が別注文で2件あっても1行・手持ち2として扱えるようにするため。
     * オプションが違えば別商品（別プール）。発行済みユニットは手持ちから除く。
     */
    productPools() {
      const issuedByOrderId = new Map();
      for (const rpo of this.receiptPaperOrders) {
        if (!rpo.order_id) {
          continue;
        }
        issuedByOrderId.set(
          rpo.order_id,
          (issuedByOrderId.get(rpo.order_id) || 0) + 1
        );
      }
      const pools = new Map();
      for (const order of this.orders) {
        const key = receiptProductKey(order.menu_id, order.option_menu_name || []);
        if (!pools.has(key)) {
          pools.set(key, {
            key,
            menuName: order.menu_name,
            optionNames: order.option_menu_name || [],
            unitPrice: order.price,
            taxRate: order.sales_tax,
            units: [],
          });
        }
        const pool = pools.get(key);
        const rest = availableQuantity(
          order.quantity,
          issuedByOrderId.get(order.id) || 0
        );
        for (let i = 0; i < rest; i++) {
          pool.units.push({
            order_id: order.id,
            price: order.prices.tanka.include,
          });
        }
      }

      return Array.from(pools.values()).filter((pool) => pool.units.length > 0);
    },
    // ODR-1704: チェック済みの商品プールと「1枚あたり個数」
    checkedProductSelections() {
      return this.productPools
        .filter((pool) => this.perReceiptOf(pool) > 0)
        .map((pool) => ({
          pool,
          units: pool.units,
          perReceipt: this.perReceiptOf(pool),
        }));
    },
    // ODR-1704: 領収書1枚あたりの額面＝この1枚で実際に受領した額。
    // 金券・その他割引は支払い手段であって現金受領ではないため差し引く
    // （ODR-1671: 金券分を含めた領収書は「受領した現金」を過大に証明してしまう）。
    // マイナス＝金券・割引が商品を上回る配分で、そのままでは発行できない。
    receiptFaceValue(): number {
      const appliedDiscount =
        this.couponDiscountPrice +
        (this.isDiscountChecked ? this.discountPrice : 0);

      return this.price + this.detailAmount - appliedDiscount;
    },
    // ODR-1704: 領収書「1枚あたり」の商品点数（チェックしたプールの1枚あたり個数の合計）
    productQuantity() {
      return this.checkedProductSelections.reduce(
        (acc, selection) => acc + selection.perReceipt,
        0
      );
    },
    // ODR-1704: 領収書「1枚あたり」の追加明細の点数（チェック済みのみ）。
    // 以前はここで selectIssued=[1]/count=1 を書き換えて「明細ありは1枚固定」にしていたが、
    // 明細も分割発行できるようになったため撤去した（getter の副作用も解消）。
    detailQuantity() {
      let quantity = 0;
      for (const detail of this.checkedReceiptDetails) {
        quantity += Number(detail.quantity);
      }
      return quantity;
    },
    // ODR-1704: 全選択＝すべての商品プールを「手持ち数ぶん」1枚に載せている状態
    // （＝発行済みを除く残り全部を1枚で発行する）。プール単位の選択状態から判定する。
    isAllSelected() {
      const pools = this.productPools;

      return (
        pools.length > 0 &&
        pools.every((pool) => this.perReceiptOf(pool) === pool.units.length)
      );
    },
    isIndeterminate() {
      const pools = this.productPools;
      const selected = pools.filter((pool) => this.perReceiptOf(pool) > 0);

      return selected.length > 0 && !this.isAllSelected;
    },
    // ODR-1704: 式の中に赤い要素（発行できない原因）が出ているか
    hasFormulaError(): boolean {
      return (
        this.receiptFormula.amountError || this.receiptFormula.discountError
      );
    },
    // ODR-1704: 金額の内訳（式）を開くか。
    // 普段は畳んでおき、発行できない状態になったら自動で開いて原因を見せる。
    // 手動で開いているあいだは、エラーが解消しても開いたままにする。
    isFormulaOpen(): boolean {
      return this.isFormulaManuallyOpened || this.hasFormulaError;
    },
  },
  watch: {
    mode: {
      handler(newVal) {
        if (newVal) {
          this.isOpen = true;
        }
      },
    },
  },
  methods: {
    // handler
    handlerOpenPopup(
      payment: Payment,
      tableSession: TableSession,
      receiptPaper: ReceiptPaper
    ) {
      this.mode = modeType.regist;
      this.receiptPaper = receiptPaper;
      this.shop = tableSession.shop;
      this.tableSession = tableSession;
      // @FIXME I want use OrderStatus.Paid
      this.orders = payment.orders.filter(order => order.status === 2) || [];
      this.staffId = this.staffId;
      this.payment = payment;
      this.paymentCoupons = payment.payment_coupons;
      this.selectedReceiptDetails = [];
      this.printedReceiptDetails = [];
      for (let i = 0; i < tableSession.payments.length; i++) {
        const papers = tableSession.payments[i].receipt_papers;
        for (let j = 0; j < papers.length; j++) {
          const details = papers[j].receipt_paper_details;
          for (let k = 0; k < details.length; k++) {
            this.printedReceiptDetails.push(details[k]);
          }
        }
      }
      this.receiptPaperOrders = [];
      for (let i = 0; i < tableSession.payments.length; i++) {
        const papers = tableSession.payments[i].receipt_papers;
        for (let j = 0; j < papers.length; j++) {
          const details = papers[j].receipt_paper_orders;
          for (let k = 0; k < details.length; k++) {
            this.receiptPaperOrders.push(details[k]);
          }
        }
      }
      this.discountPrice = payment.discount_price || 0;
      this.listChecked = [];
      this.selectIssued = [];
      this.minReceipts = 0;
      this.isDiscountChecked = false;
      this.couponDiscountPrice = 0;
      // 選択状態を初期化（再オープン時に前回の選択が残り背景ハイライト等が残るのを防ぐ）
      // ODR-1704: 商品の選択はプール単位の「1枚あたり個数」で保持する
      this.productPerReceipt = {};
      this.selectedCoupons = [];
      this.count = 0;
      this.allSelectedAutoChecked = false;
      // ODR-1704: 式の開閉も初期化する（再オープン時は既定の「閉じている」状態に戻す）
      this.isFormulaManuallyOpened = false;
      // ODR-1704: お釣りあり金券の会計は金券を外せない（全選択・1枚のみ）。
      // チェックボックスを disabled にしているので、描画後にこちらで全てチェックしておく。
      this.$nextTick(() => {
        this.forceCheckChangeableCoupons();
      });
    },
    // ODR-1704: お釣りあり金券の会計なら、金券を全てチェック済みにする。
    // 金券とお釣りはセットで初めて金額が成立するため、一部だけ回すことを許さない。
    forceCheckChangeableCoupons() {
      if (!this.hasChangeableCoupon) {
        return;
      }
      const couponChecks = Array.from(
        document.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"].CouponCheck'
        )
      );
      for (const checkbox of couponChecks) {
        checkbox.checked = true;
      }
      this.changeListChecked();
    },
    handleClickAddAdditionalDetail() {
      this.addAdditionalDetail();
    },
    // ODR-1704: 追加明細の「1枚あたり個数」を 1〜99 に収める（過大入力・過剰発行の防止）
    clampDetailQuantity(detail) {
      const quantity = Math.floor(Number(detail.quantity));
      const clamped = Number.isFinite(quantity)
        ? Math.min(RECEIPT_PAPER_DETAIL_MAX_QUANTITY, Math.max(1, quantity))
        : 1;
      this.$set(detail, "quantity", clamped);
      this.changeListChecked();
    },
    handleClickDelDetail(index): true {
      this.selectedReceiptDetails.splice(index, 1);
      // ODR-1704: 明細の増減で発行枚数・金額を再計算する
      this.changeListChecked();

      return true;
    },
    handleClickCloseModal: function () {
      this.closeModal();
    },
    handleClickSubmitReceipt: function () {
      if (this.isLoading) {
        return;
      }
      if (this.checkPrice() === false) {
        this.isLoading = false;
        return;
      }
      this.submitReceipt();
    },
    // logic
    addAdditionalDetail() {
      const currentDetail = _.cloneDeep(this.newAdditionalDetail);
      this.selectedReceiptDetails.push(currentDetail);
      // ODR-1704: 明細の増減で発行枚数・金額を再計算する
      this.changeListChecked();
    },
    filterdReceiptOther(detail, quantity, isDetailSet) {
      const code = this.shop;
      const price = Number(detail.unit_price) * quantity;
      if (code.is_naizei === true) {
        let exclude = price / ((detail.tax_rate + 100) / 100);
        switch (code.tanmatsu) {
          case 2:
            exclude = Math.floor(exclude);
            break;
          case 1:
            exclude = Math.ceil(exclude);
            break;
          default:
            exclude = Math.round(exclude);
            break;
        }
        if (isDetailSet) {
          detail.is_naizei = true;
          detail.tax = price - exclude;
          detail.price = price;
        }
        return this.numberFormat(price);
      }
      let tax = (price * detail.tax_rate) / 100;
      switch (code.tanmatsu) {
        case 1:
          tax = Math.floor(tax);
          break;
        case 2:
          tax = Math.ceil(tax);
          break;
        default:
          tax = Math.round(tax);
          break;
      }
      const total = price + tax;
      if (isDetailSet) {
        detail.is_naizei = false;
        detail.tax = tax;
        detail.price = total;
      }
      return this.numberFormat(total);
    },
    isCouponed(id): boolean {
      return this.receiptPaperOrders.some(
        (receiptPaperOrder) => receiptPaperOrder.payment_coupon_id === id
      );
    },
    isDiscounted(discountPrice): boolean {
      return this.receiptPaperOrders.some(
        (receiptPaperOrder) => receiptPaperOrder.discount_price >= discountPrice
      );
    },
    // ODR-1704: 選択状態は productPerReceipt / 明細の is_checked で保持するため、
    // 旧実装の DOM 走査（.ListCheck:checked の id を文字列パース）は廃止した。
    // 選択が変わったら金額・発行枚数を再計算する。
    changeListChecked() {
      this.autoCheckOnAllSelected();
      this.changeCouponChecked();
      this.getReceiptCount();
      if (this.isDiscountChecked) {
        this.changeDiscountChecked();
      }
    },
    // 行全体クリックでチェックをトグルする（金券名・割引名のクリック対応）。
    // 金券・その他割引は DOM のチェックボックスを直接持つため、従来どおり行内の
    // checkbox をトグルして change を発火させる（商品はプール状態なので toggleProductRow）。
    toggleRowCheck(event: Event) {
      // チェックボックス自体のクリックはネイティブ動作に任せる（二重トグル防止）
      if (this.isFormElementClick(event)) {
        return;
      }
      const row = event.currentTarget as HTMLElement;
      const checkbox = row.querySelector<HTMLInputElement>(
        'input[type="checkbox"]'
      );
      if (!checkbox || checkbox.disabled) {
        return;
      }
      checkbox.checked = !checkbox.checked;
      // 対応する @change ハンドラを発火させる
      checkbox.dispatchEvent(new Event("change"));
    },
    // すべての商品を選択した「瞬間」だけ、金券・その他割引を自動でチェックする。
    // 全選択に入った1回だけ発火し、以降は付け直さない（＝全選択中でも手動で外せる）。
    // ODR-1704: 判定は DOM ではなくプールの選択状態（isAllSelected）で行う。
    autoCheckOnAllSelected() {
      if (!this.isAllSelected) {
        // 全選択が解けたらフラグを戻す（次に全選択したら再度自動チェック）
        this.allSelectedAutoChecked = false;
        return;
      }
      // すでに一度自動チェック済みなら付け直さない（金券を手動で外せるように）
      if (this.allSelectedAutoChecked) {
        return;
      }
      this.allSelectedAutoChecked = true;
      // 金券・クーポンを自動でチェック
      const couponChecks = Array.from(
        document.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"].CouponCheck:not(:disabled)'
        )
      );
      for (const cb of couponChecks) {
        cb.checked = true;
      }
      // その他割引も自動でチェック（全選択時は割引も使い切る想定）
      if (this.discountPrice > 0 && !this.isDiscounted(this.discountPrice)) {
        this.isDiscountChecked = true;
      }
    },
    // ODR-1704: 商品プールの「1枚あたり個数」（未設定は0＝この領収書に含めない）
    perReceiptOf(pool) {
      return Number(this.productPerReceipt[pool.key]) || 0;
    },
    // ODR-1704: 商品プールのチェック切替。チェックONで1個/枚、OFFで0（含めない）。
    toggleProduct(pool) {
      const next = this.perReceiptOf(pool) > 0 ? 0 : 1;
      this.$set(this.productPerReceipt, pool.key, next);
      this.changeListChecked();
    },
    // 行内のフォーム操作（個数入力・税率セレクト等）や削除クリックは行トグルの対象外にする
    isFormElementClick(event: Event): boolean {
      const target = event.target as HTMLElement;
      if (!target || !target.tagName) {
        return false;
      }

      return ["INPUT", "SELECT", "TEXTAREA", "OPTION", "A", "LABEL"].includes(
        target.tagName
      );
    },
    // 行全体クリックで商品のチェックをトグルする（商品名クリック対応）
    toggleProductRow(pool, event: Event) {
      if (this.isFormElementClick(event)) {
        return;
      }
      this.toggleProduct(pool);
    },
    // ODR-1704: 数量欄への直接入力を productPerReceipt に反映する。
    // 直接代入（productPerReceipt[key] = value）は Vue2 では未登録キーが非リアクティブになり、
    // しかも一度生やすと後続の $set も「既存キー」と見なされ通常代入にフォールバックして
    // 復旧できない（額面・点数・発行枚数が入力値と乖離したまま固定される）。
    // 必ず $set でリアクティブに登録し、その場で発行枚数を再計算する。
    inputProductPerReceipt(pool, value) {
      this.$set(this.productPerReceipt, pool.key, value);
      this.changeListChecked();
    },
    // ODR-1704: 「1枚あたり個数」を 0〜手持ち数 に収める
    clampProductPerReceipt(pool) {
      const value = Math.floor(Number(this.productPerReceipt[pool.key]));
      const clamped = Number.isFinite(value)
        ? Math.min(pool.units.length, Math.max(0, value))
        : 0;
      this.$set(this.productPerReceipt, pool.key, clamped);
      this.changeListChecked();
    },
    getReceiptCount() {
      // ODR-1704: 追加明細だけの領収書は発行できない（商品を1つも選んでいなければ0枚）。
      // 明細は手持ち（会計上の残数）という概念が無いため、枚数を決めるのは商品のみ。
      if (this.checkedProductSelections.length === 0 && this.couponDiscountPrice === 0) {
        this.minReceipts = 0;
        this.selectIssued = [];
        this.count = 0;
        return;
      }
      // 割引券、金券を適用した場合は、領収書は1枚のみ
      // （金券・割引は「丸ごと1回」適用するもので、同一コピーに満額載せると過剰発行になるため）
      if (this.isDiscountChecked || this.couponDiscountPrice > 0) {
        // ODR-1704: 金券が商品を賄えない配分では発行できない。判定は receiptCalc に委譲し、
        // ⚠警告・発行ボタンの活性と必ず同じ条件になるようにする
        // （以前はここだけ自前の式で、警告や isReceiptable と食い違っていた）。
        if (this.receiptCalcResult.error) {
          this.minReceipts = 0;
          this.selectIssued = [];
          this.count = 0;
          return;
        }
        this.selectIssued = [1];
        this.count = 1;
        return;
      }
      // ODR-1704: お釣りあり金券の会計は分割発行できない（金券とお釣りはセットで
      // 初めて金額が成立するため）。金券は全選択のうえ1枚だけ発行する。
      if (this.hasChangeableCoupon) {
        if (this.receiptCalcResult.error) {
          this.minReceipts = 0;
          this.selectIssued = [];
          this.count = 0;
          return;
        }
        this.minReceipts = 1;
        this.selectIssued = [1];
        this.count = 1;
        return;
      }
      // ODR-1704: 同一商品（menu_id＋オプション）は注文をまたいでプールしてあるので、
      // 別注文（各数量1）でも合算した手持ちで枚数を出せる。
      const minReceipts = maxIssuableCount(
        this.checkedProductSelections.map((selection) => ({
          available: selection.units.length,
          perReceipt: selection.perReceipt,
        }))
      );
      this.minReceipts = minReceipts;
      this.selectIssued = Array.from({ length: minReceipts }, (_, i) => i + 1);
      // 未選択なら 1 枚を選択する
      this.count = this.count ? this.count : 1;
      // 選択可能数を超えた枚数が選択されていたら、選択可能数まで下げる
      this.count = (this.count <= minReceipts) ? this.count : minReceipts;
    },
    changeCouponChecked() {
      this.couponDiscountPrice = 0;
      // チェック操作のたびに再構築する（重複追加防止）
      this.selectedCoupons = [];
      let couponDiscountPrice = 0;
      // class="CouponCheck"のチェックボックスのidを取得
      const couponIds = Array.from(
        document.querySelectorAll('input[type="checkbox"].CouponCheck:checked')
      ).map((checkbox) => checkbox.id);
      // チェックが入っているidのクーポンの割引額を取得
      for (const couponId of couponIds) {
        if (!couponId) {
          continue;
        }
        const choicedCoupon = this.paymentCoupons.find(
          (coupon) => coupon.id === couponId
        );
        couponDiscountPrice += choicedCoupon.discount_price;
        this.selectedCoupons.push({
          payment_coupon_id: couponId,
          discount_price: choicedCoupon.discount_price * -1,
        });
      }
      if (this.price < 0) {
        this.showErrorNotification("金券の金額が合計金額を超えています。");
      } else {
        this.couponDiscountPrice = couponDiscountPrice;
      }
    },
    errorForm(path: string): Array<Array<string>> {
      return (
        path.split(".").reduce((acc, key) => {
          return acc ? acc[key] : undefined;
        }, this.errors) || []
      );
    },
    numberFormat(number) {
      if (typeof number !== "number" || isNaN(number)) {
        return 0;
      }
      return number.toLocaleString();
    },
    changeDiscountChecked() {
      if (!this.isDiscountChecked) {
        return;
      }
      // ODR-1704: 判定は receiptCalc に委譲する（以前はここも自前の式で、
      // 追加明細を現金側に加算していたため警告・発行枚数と条件が食い違っていた）。
      if (this.receiptCalcResult.error) {
        this.$nextTick(() => {
          this.isDiscountChecked = false;
        });
        this.minReceipts = 0;
        this.selectIssued = [];
        this.count = 0;
        this.showErrorNotification(
          RECEIPT_CALC_MESSAGES[this.receiptCalcResult.error]
        );

        return;
      }
      // 割引券を適用した場合は、領収書は1枚のみ
      this.selectIssued = [1];
      this.count = 1;
    },
    closeModal() {
      this.isLoading = false;
      this.mode = undefined;
      this.isOpen = false;
      this.order = {};
      this.count = 0;
      this.$emit("close");
    },
    statusAttr(): string {
      return this.isFinish ? "success" : "info";
    },
    // 発行不可の理由（ODR-1671）。金券・その他割引は複数の領収書に配分できるが過大発行はしない。
    //  (a) お預かり(現金) < 0 … この1枚で金券・割引が商品を上回る
    //  (b) 残り発行可能 < 0 … 残りの商品で残りの金券・割引を使い切れない配分
    // 問題があればエラーメッセージ、無ければ null を返す。
    bundleError(): string | null {
      const e = this.receiptCalcResult.error;
      return e ? RECEIPT_CALC_MESSAGES[e] : null;
    },
    checkPrice: function () {
      // お預かり(現金)≧0 かつ 残り発行可能≧0 でないと発行不可（ODR-1671）
      const bundleError = this.bundleError();
      if (bundleError) {
        this.showErrorNotification(bundleError);

        return false;
      }
      // ODR-1704: 発行可能額（グロス）の上限が効くのは会計の商品側のみ。
      // 追加明細は別決済済みの外部明細で会計の発行可能額を消費しない（上限側にも同額入るため相殺）。
      const selectedPrice = this.payment.receiptable_price;

      let price = this.price;
      if (this.isDiscountChecked) {
        price -= this.discountPrice;
      }
      if (price * this.count > selectedPrice) {
        if (price * this.count - selectedPrice === this.discountPrice && this.discountPrice > 0 && !this.isDiscountChecked) {
          this.showErrorNotification(translate("hall-0290", "その他割引・クーポン を適用した領収書から発行してください"));
        } else {
          this.showErrorNotification(translate("hall-0285", "発行枚数 が正しくありません"));
        }
        return false;
      }
      if (this.count === 0) {
        return false;
      }
      return true;
    },

    // api
    submitReceipt() {
      let message = "";
      let price = this.price + this.detailAmount;
      let discount = [];
      if (this.isDiscountChecked) {
        price -= this.discountPrice;
        discount.push({
          discount_price: this.discountPrice,
        });
      }
      // ODR-1704: 発行枚数ぶんのコピーそれぞれに、実際に使う注文ユニットを割り当てる。
      // 同一商品（menu_id＋オプション）は注文をまたいでプールしてあるので、
      // 別注文（各数量1）でも 1枚目=注文A / 2枚目=注文B のように配分される。
      const copies = allocateReceiptOrders(
        this.checkedProductSelections.map((selection) => ({
          units: selection.units,
          perReceipt: selection.perReceipt,
        })),
        this.count
      );
      // ODR-1704: 追加明細（チェック済みのみ・1枚あたり個数）は全コピーに載せる。
      // is_checked は画面用のフラグなので送信データからは除く。
      const details = this.checkedReceiptDetails.map(
        ({ is_checked, ...detail }) => detail
      );
      // 金券・その他割引は「丸ごと1回」適用のため、適用時は発行枚数が1枚に制限される。
      const receiptPapers = copies.map((orders) => ({
        table_session_id: this.tableSession.id,
        staff_id: this.staffId,
        payment_id: this.payment.id,
        print_job_id: null,
        price,
        receipt_paper_details: details,
        receipt_paper_orders: [...orders, ...this.selectedCoupons, ...discount],
      })) as ReceiptPaper[];

      api
        .receipt_receipt({
          receipt_papers: receiptPapers,
          print_rule_id:
            store.state.printRule[PrintBit.TYPE_SETTLE_AND_RECEIPT],
        })
        .then(() => {
          this.mode = modeType.finish;
          this.$emit("registered");
        })
        .catch((err) => {
          message =
            err.response.data.message ||
            translate("hall-0046", "保存に失敗しました。");
          this.showErrorNotification(message);
          this.errors.payment = message;
          this.errors.receipt_paper = err.response.data.errors.receipt_paper;
          this.isLoading = false;
        });
    },
    // ODR-1704: 全選択トグル。全選択中なら全解除、そうでなければ
    // 各商品プールを「手持ち数ぶん」1枚に載せる（＝残り全部を1枚で発行）。
    toggleAll() {
      if (this.isAllSelected) {
        this.uncheckAllListItems();
      } else {
        this.checkAllListItems();
      }
      this.changeListChecked();
    },
    checkAllListItems() {
      for (const pool of this.productPools) {
        this.$set(this.productPerReceipt, pool.key, pool.units.length);
      }
    },
    uncheckAllListItems() {
      for (const pool of this.productPools) {
        this.$set(this.productPerReceipt, pool.key, 0);
      }

      // 全選択解除では金券・クーポンも外す（autoCheckOnAllSelected の対称）。
      // ODR-1704: お釣りあり金券は外せない（disabled なのでここでも対象外になる）。
      const couponChecks = Array.from(
        document.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"].CouponCheck'
        )
      );
      couponChecks.forEach((checkbox) => {
        if (!checkbox.disabled) {
          checkbox.checked = false;
        }
      });

      // その他割引も外す
      if (!this.isDiscounted(this.discountPrice)) {
        this.isDiscountChecked = false;
      }
    },
  },
});
